import React, { useEffect, useMemo, useRef, useState } from "react";

import { Box, Drawer, IconButton, useMediaQuery, useTheme, Popover, Alert, Typography, Button } from "@mui/material";
import {
	Close,
	Settings,
	SpaceDashboard,
	Share,
	Check,
	SentimentDissatisfied,
	SentimentVeryDissatisfied,
	SentimentNeutral,
	SentimentSatisfied,
	SentimentVerySatisfied
} from "@mui/icons-material";
import {
	Template01,
	Template02,
	Template03,
	Template04,
	Template05,
	Template06,
	Template07,
	Template08
} from "../../Components/Templates/WeddingTemplates";

import {
	IfieldUpdate,
	IGallery,
	INVITATION_PRICING_PLANS_ENUM,
	INVITATION_STATUS,
	Invitations,
	TEMPLATES
} from "../../store/services/invitations/dto/invitations.dto";

import {
	useLazyGetManageWeddingInvitationGalleryQuery,
	useLazyGetWeddingInvitationCommentsQuery
} from "../../store/services/invitations/invitations.service";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { EditFormWrapper, WeddingInvitationContainer, WhiteCrown } from "./styles";

import InvitationLoader from "../../Components/InvtationLoader/InvitationLoader";
import NoDataFallback from "../../Components/NoDataFallback";

import EditPanel from "./EditPanel";
import { useAppDispatch, useAppSelector } from "../../store";
import {
	deSelectField,
	getFullInvitationsState,
	setSelectedField
} from "../../store/slices/invitations/invitations.slice";
import { getFullState as getFullAuthState, setRunGuide } from "../../store/slices/auth/auth.slice";
import { getFieldMap } from "../../utils/inputs";
import EditWrapper from "../../Components/HOC/EditWrapper";
import ShareWeddingInvitationModal from "../../Components/Modals/ShareModal";
import { trackEvent } from "../../utils/analytics";
import { Tooltip } from "react-tooltip";
import StatusControl from "./StatusControl";
import { common, red, orange, blue, green } from "@mui/material/colors";
import Joyride, { ACTIONS, EVENTS, ORIGIN, STATUS, CallBackProps } from "react-joyride";
import { useLazyFetchUserDataQuery, useUpdateUserAccountMutation } from "../../store/services/auth/auth.service";
import { notifyError } from "../../utils/NotifyError";
import { IError } from "../../utils/getErrorMessage";
import ProgressWithLabel from "../../Components/ProgressWithLabel";
import { CircularProgressProps } from "@mui/material/CircularProgress";
import { useLazyGetDeviceSubscriptionQuery } from "../../store/services/notifications/notifications.service";
import { getOrCreateAnonId } from "../../utils/getOrCreateAnonId";
import NotificationsConfig from "../../Components/Modals/NotificationsConfig";
import { InvitationColorMatrix } from "../../InvitationColorMatrix";
import { PLAN_NAMES } from "../../constants";
const EDIT_PATH = "manageWeddingInvitation";

enum GUIDE_STEPS {
	guide_edit_field = 1,
	guide_field_update = 2,
	guide_notch = 3
}

const MIN_PROGRESS = 40;
const PROGRESS_CHECKS = [
	{
		label: "Update Bride Name",
		field: "bride_name",
		fieldMap: "BRIDE_NAME",
		updated: false
	},
	{
		label: "Update Groom Name",
		field: "groom_name",
		fieldMap: "GROOM_NAME",
		updated: false
	},
	{
		label: "Update Wedding Date",
		field: "wedding_date",
		fieldMap: "WEDDING_DATE",
		updated: false
	},
	{
		label: "Update Wedding Address",
		field: "wedding_location_text",
		fieldMap: "LOCATION_TEXT",
		updated: false
	},
	{
		label: "Update Wedding Location",
		field: "wedding_coordinates",
		fieldMap: "MAP",
		updated: false
	},
	{
		label: "Update Main Image",
		field: "main_image",
		fieldMap: "MAIN_IMAGE",
		updated: false
	},
	{
		label: "Update Bride's Image",
		field: "bride_image",
		fieldMap: "BRIDE_IMAGE",
		updated: false
	},
	{
		label: "Update Groom's Image",
		field: "groom_image",
		fieldMap: "GROOM_IMAGE",
		updated: false
	},
	{
		label: "Update Bride's Social Media Links",
		field: "partner_socials_1",
		fieldMap: "SOCIAL_MEDIA_1",
		updated: false
	},
	{
		label: "Update Groom’s Social Media Links",
		field: "partner_socials_2",
		fieldMap: "SOCIAL_MEDIA_2",
		updated: false
	}
];
const WeddingInvitation = () => {
	const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
	const [shareInvitation, setShareInvitation] = useState(false);
	const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
	const [stepIndex, setStepIndex] = useState(0);
	const [openEnableNotifications, setOpenEnableNotifications] = useState(false);
	const [initialLoading, setInitialLoading] = useState(true);
	const [progressChecks, setProgressChecks] = useState(PROGRESS_CHECKS);
	const theme = useTheme();
	const isBiggerScreen = useMediaQuery(theme.breakpoints.up("lg"));
	const { managedInvitation, loadingManageInvitation, selectedField, invitationPayedData, errorManagedInvitation } =
		useAppSelector(getFullInvitationsState);

	const { user, guideSteps, runGuide } = useAppSelector(getFullAuthState);
	const navigate = useNavigate();
	const params = useParams();
	const dispatch = useAppDispatch();
	const id = params.id;
	const isNotificationAlertHidden = localStorage.getItem(`hide_notifications_alert_${id}`);
	const [shouldHideNotificationsAlert, setShouldHideNotificationsAlert] = useState(!!isNotificationAlertHidden);
	const [underReviewBanner, setUnderReviewBanner] = useState(true);
	const anonymous_token = getOrCreateAnonId(id || "");
	const { pathname } = useLocation();
	const canEdit = pathname.includes(EDIT_PATH);
	const [fetchGallery, { data: galleryData }] = useLazyGetManageWeddingInvitationGalleryQuery();
	const [callFetchSub, { data: deviceSub }] = useLazyGetDeviceSubscriptionQuery();
	const [fetchComments] = useLazyGetWeddingInvitationCommentsQuery();
	const [callUpdateAccount] = useUpdateUserAccountMutation();
	const [callWhoami] = useLazyFetchUserDataQuery();
	const fieldMap = getFieldMap(id as string);
	const open = Boolean(anchorEl);
	const popOverId = open ? "progress-invitation" : undefined;
	const templateRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		if (managedInvitation) {
			setSelectedTemplateId(managedInvitation.template_id);
			if (managedInvitation.fieldUpdate) {
				for (let check in managedInvitation.fieldUpdate) {
					setProgressChecks(prevState => {
						const oldState = [...prevState];
						return oldState.map(oldCheck => {
							if (oldCheck.field === check) {
								return {
									...oldCheck,
									updated: managedInvitation.fieldUpdate[check as keyof IfieldUpdate]
								};
							}
							return oldCheck;
						});
					});
				}
			}

			// filter images from progress list
			if (!managedInvitation.has_images) {
				setProgressChecks(checks => checks.filter(check => !check.field.includes("image")));
			}
			setInitialLoading(false);
		}
	}, [managedInvitation]);

	useEffect(() => {
		if (errorManagedInvitation) {
			setInitialLoading(false);
		}
	}, [errorManagedInvitation]);

	useEffect(() => {
		fetchGallery({ id: id || "" });
		fetchComments({ id: id || "" });

		return () => {
			dispatch(deSelectField());
			setSelectedTemplateId(null);
		};
	}, [id]);

	useEffect(() => {
		callFetchSub({ id: id || "", email: user?.email || "", anonymous_token }).then(({ data, error }) => {
			if (data) {
				console.log(data);
			}
			if (error) {
				console.log("error", error);
			}
		});
	}, [id]);

	useEffect(() => {
		if (initialLoading) return;
		const container = templateRef.current;

		if (!container) return;
		let activeElement: HTMLElement | null = null;
		const handleClick = (e: MouseEvent) => {
			const target = e.target as HTMLElement;

			const editableElement = target.closest<HTMLElement>("[data-dispatch-id]");

			if (!editableElement) return;

			// remove previous active
			activeElement?.classList.remove("active-field");

			// add new active
			editableElement.classList.add("active-field");

			// save reference
			activeElement = editableElement;

			const fieldId = editableElement.dataset.dispatchId;
			console.log(fieldId);
			if (!fieldId) return;

			dispatch(setSelectedField(fieldId));
		};

		container.addEventListener("click", handleClick);

		return () => {
			container.removeEventListener("click", handleClick);
		};
	}, [initialLoading]);

	const isGuideMode = useMemo(() => {
		const isGuideSkipped = localStorage.getItem("guide_skipped") || "false";
		return user && !user.guide_completed && isGuideSkipped === "false";
	}, [user, runGuide]);

	useEffect(() => {
		const timeoutHandler = setTimeout(() => {
			if (isGuideMode) {
				dispatch(setRunGuide(true));
			}
		}, 1000);
		return () => clearTimeout(timeoutHandler);
	}, [isGuideMode]);

	const template: React.ReactElement = useMemo(() => {
		if (!selectedTemplateId) {
			return <InvitationLoader />;
		}
		const invitationData = managedInvitation as Invitations;
		const color_id = invitationData?.template_color || 1;
		const _list: Record<number, React.ReactElement> = {
			[TEMPLATES.TEMPLATES01]: (
				<Template01
					data={invitationData}
					gallery={galleryData?.gallery as IGallery[]}
					canEdit={canEdit}
					colorMatrix={InvitationColorMatrix[TEMPLATES.TEMPLATES01][color_id]}
				/>
			),
			[TEMPLATES.TEMPLATES02]: (
				<Template02
					data={invitationData}
					gallery={galleryData?.gallery as IGallery[]}
					canEdit={canEdit}
					colorMatrix={InvitationColorMatrix[TEMPLATES.TEMPLATES02][color_id]}
				/>
			),
			[TEMPLATES.TEMPLATES03]: (
				<Template03
					data={invitationData}
					gallery={galleryData?.gallery as IGallery[]}
					canEdit={canEdit}
					colorMatrix={InvitationColorMatrix[TEMPLATES.TEMPLATES03][color_id]}
				/>
			),
			[TEMPLATES.TEMPLATES04]: (
				<Template04
					data={invitationData}
					gallery={galleryData?.gallery as IGallery[]}
					canEdit={canEdit}
					colorMatrix={InvitationColorMatrix[TEMPLATES.TEMPLATES04][color_id]}
				/>
			),
			[TEMPLATES.TEMPLATES05]: (
				<Template05
					data={invitationData}
					gallery={galleryData?.gallery as IGallery[]}
					canEdit={canEdit}
					colorMatrix={InvitationColorMatrix[TEMPLATES.TEMPLATES05][color_id]}
				/>
			),
			[TEMPLATES.TEMPLATES06]: (
				<Template06
					data={invitationData}
					gallery={galleryData?.gallery as IGallery[]}
					canEdit={canEdit}
					colorMatrix={InvitationColorMatrix[TEMPLATES.TEMPLATES06][color_id]}
				/>
			),
			[TEMPLATES.TEMPLATES07]: (
				<Template07
					data={invitationData}
					gallery={galleryData?.gallery as IGallery[]}
					canEdit={canEdit}
					colorMatrix={InvitationColorMatrix[TEMPLATES.TEMPLATES07][color_id]}
				/>
			),
			[TEMPLATES.TEMPLATES08]: (
				<Template08
					data={invitationData}
					gallery={galleryData?.gallery as IGallery[]}
					canEdit={canEdit}
					colorMatrix={InvitationColorMatrix[TEMPLATES.TEMPLATES08][color_id]}
				/>
			)
		};

		return _list[selectedTemplateId];
	}, [selectedTemplateId, managedInvitation, galleryData, selectedField, canEdit]);

	const progressPercentage = useMemo(() => {
		const completedCount = progressChecks.filter(item => item.updated).length;
		const totalCount = progressChecks.length;
		return Math.round((completedCount / totalCount) * 100);
	}, [progressChecks]);

	const ProgressWithLabelProps = useMemo(() => {
		const _percentage = Number(progressPercentage.toFixed(0) || 0);
		let _color: string = red[400];
		if (_percentage >= 40 && _percentage <= 50) {
			_color = orange[400];
		} else if (_percentage > 50 && _percentage <= 60) {
			_color = blue[400];
		} else if (_percentage >= 60 && _percentage <= 80) {
			_color = green[400];
		} else if (_percentage >= 80) {
			_color = green[800];
		}
		return { value: _percentage, sx: { color: _color } } as CircularProgressProps & { value: number };
	}, [progressPercentage]);

	const disablePublishBtn = useMemo(() => {
		if (!managedInvitation) return true;
		return (
			managedInvitation?.status !== INVITATION_STATUS.PUBLISHED &&
			(!managedInvitation?.fieldUpdate?.groom_name ||
				!managedInvitation?.fieldUpdate?.bride_name ||
				!managedInvitation?.fieldUpdate?.wedding_date ||
				(!managedInvitation?.fieldUpdate?.wedding_coordinates &&
					!managedInvitation?.fieldUpdate?.wedding_location_text))
		);
	}, [managedInvitation]);

	const handleProgressClick = (event: React.MouseEvent<HTMLDivElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const getContent = () => {
		if (errorManagedInvitation && !loadingManageInvitation) {
			return <NoDataFallback />;
		}
		if (initialLoading) {
			return <InvitationLoader />;
		}

		const handleDrawerClose = () => {
			dispatch(deSelectField());
		};

		const getProgressIcon = (percentage: number) => {
			if (percentage < 40) {
				return <SentimentVeryDissatisfied style={{ fontSize: "38px", color: red[400] }} />;
			} else if (percentage <= 50) {
				return <SentimentDissatisfied style={{ fontSize: "38px", color: orange[400] }} />;
			} else if (percentage <= 60) {
				return <SentimentNeutral style={{ fontSize: "38px", color: blue[400] }} />;
			} else if (percentage <= 80) {
				return <SentimentSatisfied style={{ fontSize: "38px", color: green[400] }} />;
			}
			return <SentimentVerySatisfied style={{ fontSize: "38px", color: green[800] }} />;
		};

		return (
			<Box className={"invitation-container"}>
				{selectedField && isBiggerScreen && (
					<EditFormWrapper>
						<EditPanel selectedField={selectedField} />
					</EditFormWrapper>
				)}
				<Drawer anchor={"bottom"} open={!!(selectedField && !isBiggerScreen)} onClose={handleDrawerClose}>
					<Box className={"mobile_drawer_wrapper"}>
						<Box
							display="flex"
							justifyContent="flex-end"
							style={{ position: "sticky", background: common.white, right: "5px", top: "0", zIndex: 2 }}
						>
							<IconButton onClick={handleDrawerClose}>
								<Close />
							</IconButton>
						</Box>
						<EditPanel isSmallScreen={true} selectedField={selectedField as string} postSave={handleDrawerClose} />
					</Box>
				</Drawer>
				<Box className={"invitation-wrapper"} ref={templateRef}>
					<Box className={"notification_alert"}>
						{(!deviceSub || !deviceSub?.subscribed) && !shouldHideNotificationsAlert && !runGuide && (
							<Alert
								severity={"info"}
								style={{ marginBottom: "2px" }}
								action={
									<Box display={"flex"} alignItems={"center"} gap={1} p={0}>
										<Button onClick={() => setOpenEnableNotifications(true)} style={{ fontSize: 14 }}>
											Settings
										</Button>
										<Button
											onClick={() => {
												setShouldHideNotificationsAlert(true);
												localStorage.setItem(`hide_notifications_alert_${id}`, "true");
											}}
											style={{ fontSize: 14 }}
										>
											Close
										</Button>
									</Box>
								}
							>
								<Typography fontSize={14}>Enable notifications</Typography>
							</Alert>
						)}
						{managedInvitation?.status === INVITATION_STATUS.PENDING && underReviewBanner && (
							<Alert severity={"info"} onClose={() => setUnderReviewBanner(false)} style={{ marginBottom: "2px" }}>
								<Typography fontSize={18}>Invitation Under Review </Typography>
								<Typography fontSize={14}>
									You’ll be notified by email once it’s approved. This process may take up to 24 hours.
								</Typography>
							</Alert>
						)}

						{managedInvitation?.status === INVITATION_STATUS.ACTION_REQUIRED && (
							<Alert style={{ marginBottom: "2px" }} severity={"warning"}>
								<Typography fontSize={14}>
									Action required: We’ve sent you an email with the updates needed to publish your invitation. Please
									check your inbox.
								</Typography>
							</Alert>
						)}
					</Box>
					<Box className={"control_notch"}>
						<Box
							className={"settings_btn guide_settings_btn"}
							data-tooltip-id="settings_btn_tooltip"
							data-tooltip-content="Settings"
							data-tooltip-place="bottom"
						>
							<EditWrapper canEdit={false} dataId={fieldMap.SETTINGS}>
								<IconButton>
									<Settings />
								</IconButton>
							</EditWrapper>
						</Box>
						{(managedInvitation?.status === INVITATION_STATUS.PUBLISHED || isGuideMode || runGuide) && (
							<Box
								className={"dashboard_btn guide_dashboard_btn"}
								data-tooltip-id="dashboard_btn_tooltip"
								data-tooltip-content="Dashboard"
								data-tooltip-place="bottom"
							>
								<IconButton
									onClick={() => {
										if (isGuideMode) return;
										navigate(`/manageWeddingInvitation/${id}/dashboard`);
									}}
								>
									<SpaceDashboard />
								</IconButton>
							</Box>
						)}

						{(managedInvitation?.status === INVITATION_STATUS.PUBLISHED || isGuideMode || runGuide) && (
							<Box
								className={"share_btn guide_share_btn"}
								data-tooltip-id="share_btn_tooltip"
								data-tooltip-content="Share"
								data-tooltip-place="bottom"
							>
								<IconButton
									onClick={() => {
										if (isGuideMode) return;
										trackEvent("share_invitation_button", {
											scope: "Invitations",
											description: "Share Invitation"
										});
										setShareInvitation(true);
									}}
								>
									<Share />
								</IconButton>
							</Box>
						)}
						<Box
							className={"progress progress_settings_btn"}
							onClick={handleProgressClick}
							data-tooltip-id="completion_progress_tooltip"
							data-tooltip-content={`${progressPercentage}% completed`}
							data-tooltip-place="bottom"
						>
							{getProgressIcon(progressPercentage)}
						</Box>
						{invitationPayedData.type > INVITATION_PRICING_PLANS_ENUM.FREE && (
							<Box
								className={"plan_btn"}
								data-tooltip-id="plan_tooltip"
								data-tooltip-content={`${PLAN_NAMES[invitationPayedData.type]}`}
								data-tooltip-place="bottom"
							>
								<IconButton style={{ cursor: "unset" }}>
									<WhiteCrown />
								</IconButton>
							</Box>
						)}
					</Box>

					<Tooltip id="plan_tooltip" style={{ backgroundColor: "#789DBC", zIndex: 1000, maxWidth: "300px" }} />
					<Tooltip id="settings_btn_tooltip" style={{ backgroundColor: "#789DBC", zIndex: 1000, maxWidth: "300px" }} />
					<Tooltip id="dashboard_btn_tooltip" style={{ backgroundColor: "#789DBC", zIndex: 1000, maxWidth: "300px" }} />
					<Tooltip id="share_btn_tooltip" style={{ backgroundColor: "#789DBC", zIndex: 1000, maxWidth: "300px" }} />
					<Tooltip
						id="completion_progress_tooltip"
						style={{ backgroundColor: "#789DBC", zIndex: 1000, maxWidth: "300px" }}
					/>
					{template}
					<StatusControl disabledPublish={disablePublishBtn} />
					<Box className={"guide_welcome"} />
					<Popover
						id={popOverId}
						open={open}
						anchorEl={anchorEl}
						onClose={handleClose}
						anchorOrigin={{
							vertical: "bottom",
							horizontal: "left"
						}}
						className={"progress_popover"}
						disablePortal={true}
					>
						<Box className={"title_wrapper"}>
							<ProgressWithLabel props={ProgressWithLabelProps} />
							<Typography>Invitation Strength</Typography>
						</Box>
						{progressChecks.map((CheckUpdate, checkIndex) => (
							<Alert
								key={CheckUpdate.field}
								icon={CheckUpdate.updated ? <Check fontSize="inherit" /> : <Close fontSize="inherit" />}
								severity={CheckUpdate.updated ? "success" : "error"}
								style={
									checkIndex === progressChecks.length - 1
										? { padding: "0px 16px", paddingBottom: "8px", cursor: "pointer" }
										: { padding: "0px 16px", cursor: "pointer" }
								}
								onClick={() => {
									dispatch(setSelectedField(fieldMap[CheckUpdate.fieldMap as keyof typeof fieldMap]));
									handleClose();
								}}
							>
								{CheckUpdate.label}
							</Alert>
						))}
					</Popover>
				</Box>
			</Box>
		);
	};

	const clickElement = (classname: string) => {
		const element = document.querySelector(classname);
		if (element instanceof HTMLElement) {
			element.click();
		} else {
			console.warn("guide_edit_field not found");
		}
	};

	const handleJoyrideCallback = (data: CallBackProps) => {
		const { action, index, origin, status, type } = data;

		if (action === ACTIONS.CLOSE && (origin === ORIGIN.BUTTON_CLOSE || origin === ORIGIN.OVERLAY)) {
			// skip guide
			dispatch(setRunGuide(false));
			setStepIndex(0);
			return localStorage.setItem("guide_skipped", "true");
		}

		if (([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND] as string[]).includes(type)) {
			const triggerNextStep = () => setStepIndex(index + (action === ACTIONS.PREV ? -1 : 1));
			// trigger action
			let delayRun = 0;
			if (index === GUIDE_STEPS.guide_edit_field && action !== ACTIONS.PREV) {
				delayRun = 300;
				clickElement(".guide_edit_field");
			}
			if (index === GUIDE_STEPS.guide_field_update && action === ACTIONS.PREV) {
				dispatch(deSelectField());
			}

			setTimeout(() => {
				triggerNextStep();
			}, delayRun);
		} else if (([EVENTS.STEP_BEFORE] as string[]).includes(type)) {
			if (index === GUIDE_STEPS.guide_notch && action === ACTIONS.CLOSE) {
				dispatch(deSelectField());
			}
		} else if (status === STATUS.FINISHED) {
			dispatch(setRunGuide(false));
			setStepIndex(0);
			// handle update complete guide
			if (user?.guide_completed) {
				return;
			}
			callUpdateAccount({ body: { guide_completed: true } }).then(({ data, error }) => {
				if (data) {
					callWhoami();
				}
				if (error) {
					notifyError(error as IError);
				}
			});
		}

		// console.groupCollapsed(type);
		// console.log(data); //eslint-disable-line no-console
		// console.groupEnd();
	};

	return (
		<WeddingInvitationContainer pt={7} $showEditPanel={!!selectedField && isBiggerScreen}>
			<Joyride
				run={runGuide}
				steps={guideSteps}
				styles={{
					options: { primaryColor: theme.palette.primary.main, zIndex: 1201 }
				}}
				callback={handleJoyrideCallback}
				stepIndex={stepIndex}
				scrollOffset={200}
			/>
			{getContent()}
			{shareInvitation && (
				<ShareWeddingInvitationModal
					data={managedInvitation as Invitations}
					open={true}
					onClose={() => setShareInvitation(false)}
				/>
			)}
			{openEnableNotifications && <NotificationsConfig onClose={() => setOpenEnableNotifications(false)} />}
		</WeddingInvitationContainer>
	);
};

export default WeddingInvitation;
